import { useTranslation } from 'next-i18next';
import React from 'react';

import classNames from 'classnames';
import Image from 'next/image';
import PropTypes from 'prop-types';
import useIntersectionObserver from '../../hooks/useIntersectionObserver';
import { trackContactLead } from '../../utils/dataLayer';
import ButtonPrimary from '../ButtonPrimary';
import ButtonSecondary from '../ButtonSecondary';
import PhotoCred from '../PhotoCred';
import styles from './OfferIntro.module.scss';
import useWindowWidthTitles from '../../hooks/useWindowWidthTitles';

const OfferIntro = ({ titleTop = '', titleBottom = '', text = '', image, richtext, lastUpdated, share, isDesktop }) => {
    const [ref, isVisible] = useIntersectionObserver();
    const [imageRef, imageIsVisible] = useIntersectionObserver();
    const imageGridClasses = classNames('grid', {
        [styles['ImageGrid']]: true,
        [styles['ImageVisible']]: imageIsVisible,
    });

    const width = useWindowWidthTitles(isDesktop);

    const isMobile = width < 768;

    const titleContainerClasses = classNames(styles['TitleContainer'], {
        [styles['RichText']]: richtext,
    });

    const textClasses = classNames(styles['Text'], { [styles['RichText']]: richtext });
    const classes = classNames(styles['Root'], {
        [styles['Visible']]: isVisible,
        [styles['IsFromRichText']]: richtext,
    });

    const buttonContainerClasses = classNames(styles['ButtonContainer'], {
        [styles['ButtonContainerRichText']]: richtext,
    });
    const imageStyle = {
        objectFit: 'cover',
    };

    if (!!image && !!image.focal && !!image.focal.x && !!image.focal.y) {
        imageStyle.objectPosition = `${image.focal.x} ${image.focal.y}`;
    }

    const { t } = useTranslation();
    return (
        <section className={classes} ref={ref}>
            <div ref={imageRef} className={imageGridClasses}>
                {!!image?.url && (
                    <div className={`${styles['ImageContainer']}`}>
                        <Image
                            className={styles['Image']}
                            src={image.url}
                            alt={image?.alt}
                            title={image?.title}
                            fill
                            style={imageStyle}
                        />
                        <PhotoCred credit={image?.credit} />
                    </div>
                )}
            </div>

            <div className={`${styles['Content']} grid`}>
                {!richtext ? (
                    <>
                        {isMobile ? (
                            <h2 className={`${styles['Title']} ${styles['TitleMobile']} grid`}>
                                <span className={styles['TitleContainer']}>
                                    <span
                                        className={styles['TitleTop']}
                                        dangerouslySetInnerHTML={{ __html: titleTop }}
                                    />{' '}
                                    <span
                                        className={styles['TitleBottom']}
                                        dangerouslySetInnerHTML={{ __html: titleBottom }}
                                    />
                                </span>
                            </h2>
                        ) : (
                            <h2 className={`${styles['Title']} ${styles['TitleDesktop']} grid`}>
                                <span className={styles['TitleTop']}>{titleTop}</span>
                                <span className={styles['TitleBottom']}>{titleBottom}</span>
                            </h2>
                        )}
                    </>
                ) : (
                    <>
                        {isMobile ? (
                            <h1 className={`${styles['Title']} ${styles['TitleMobile']} grid`}>
                                <span className={titleContainerClasses}>
                                    <span className={styles['TitleTop']}>
                                        {titleTop}
                                        <span className={styles['TitleBottom']}> {titleBottom}</span>
                                    </span>
                                </span>
                            </h1>
                        ) : (
                            <h1 className={`${styles['Title']} ${styles['TitleDesktop']} grid`}>
                                <span className={titleContainerClasses}>
                                    <span className={styles['TitleTop']}>
                                        {titleTop}
                                        <span className={styles['TitleBottom']}> {titleBottom}</span>
                                    </span>
                                </span>
                            </h1>
                        )}
                    </>
                )}

                <div className={`grid`}>
                    <p className={textClasses}>{text}</p>
                </div>
            </div>
            <div className="grid">
                <div className={buttonContainerClasses}>
                    {!richtext ? (
                        <ButtonPrimary
                            modifiers={['InlineFlex', 'IconRotate45']}
                            icon="icon-arrow-right-white"
                            onClick={() => {
                                trackContactLead('Offer Intro');
                                document.getElementById('footer-contact').scrollIntoView({
                                    behavior: 'smooth',
                                });
                            }}>
                            {t('Generic.contactUs')}
                        </ButtonPrimary>
                    ) : (
                        <ButtonSecondary
                            modifiers={['InlineFlex', 'IconRotate45', 'Share']}
                            icon="icon-share-white"
                            onClick={share.onClick}>
                            {t('Generic.share')}
                        </ButtonSecondary>
                    )}
                </div>
            </div>
        </section>
    );
};

OfferIntro.propTypes = {
    titleTop: PropTypes.string,
    titleBottom: PropTypes.string,
    text: PropTypes.string,
};

export default OfferIntro;
